/**
 * DemoActivatePage (iter-198)
 *
 * Public route /demo/activate/:token. When a visitor clicks the
 * activation link from the admin-approved demo email, this page:
 *   1. Calls GET /api/public/demo/activate/{token}.
 *   2. Persists the returned access_token in tokenStore.
 *   3. Redirects to /dashboard so AuthContext boots them
 *      authenticated into their freshly-seeded demo workspace.
 *
 * Failure states (link expired, invalid token, server error) are
 * surfaced inline with a friendly message and a CTA back to the
 * landing page.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { tokenStore } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loader2, AlertCircle, PartyPopper } from "lucide-react";

export default function DemoActivatePage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", error: null });
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/demo/activate/${token}`, {
          withCredentials: true,
        });
        if (cancelled) return;
        if (r.data?.access_token) {
          tokenStore.set(r.data.access_token);
        }
        setState({ status: "ok", error: null });
        setTimeout(() => {
          // Hard reload so AuthContext re-bootstraps from the cookie+token.
          window.location.assign(r.data?.redirect || "/dashboard");
        }, 800);
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        let msg = "No pudimos activar tu demo. Probá de nuevo en unos minutos.";
        if (code === 404) msg = "Link inválido. Verificá que copiaste todo el enlace del email.";
        else if (code === 410 || detail === "link_expired") msg = "El link de activación expiró. Pedí uno nuevo respondiendo al email de aprobación.";
        else if (code === 400 && detail === "request_not_active") msg = "Esta solicitud no está aprobada o ya fue procesada.";
        setState({ status: "error", error: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [token, API]);

  return (
    <div className="min-h-screen bg-cream-100 text-slate-900 flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center" data-testid="demo-activate-page">
        {state.status === "loading" && (
          <div data-testid="demo-activate-loading">
            <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto" />
            <h1 className="font-serif-display text-2xl mt-6">Activando tu demo…</h1>
            <p className="text-slate-500 mt-2 text-sm">Estamos preparando tu workspace con datos a tu medida.</p>
          </div>
        )}
        {state.status === "ok" && (
          <div data-testid="demo-activate-ok">
            <PartyPopper className="h-10 w-10 text-emerald-600 mx-auto" />
            <h1 className="font-serif-display text-2xl mt-6">¡Demo activo!</h1>
            <p className="text-slate-500 mt-2 text-sm">Te llevamos a tu dashboard ahora…</p>
          </div>
        )}
        {state.status === "error" && (
          <div data-testid="demo-activate-error">
            <AlertCircle className="h-10 w-10 text-amber-600 mx-auto" />
            <h1 className="font-serif-display text-2xl mt-6">No pudimos activar tu demo</h1>
            <p className="text-slate-600 mt-3 text-sm">{state.error}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-7">
              <Link to="/">
                <Button data-testid="demo-activate-home" variant="outline" className="rounded-full px-6">Volver al inicio</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="demo-activate-register" className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6">Crear cuenta gratis</Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
